/**
 * Quais pontos de uma linha ganham número (26/10/2026): o último com dado e os extremos (maior e
 * menor). Com "Mostrar valores" ligado, todos. Regra de legibilidade: rótulo em todo ponto de
 * uma série diária não é lido por ninguém.
 */

const valido = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/**
 * Índices do último ponto com dado, do maior e do menor. O menor ignora o zero: domingo e feriado
 * sem produção não são o "pior dia", e o rótulo "0" no meio do gráfico não diz nada. Série sem
 * dado: nenhum.
 */
export function pontosEmDestaque(valores: ReadonlyArray<unknown>): Set<number> {
  let ultimo = -1;
  let maior = -1;
  let menor = -1;
  valores.forEach((v, i) => {
    if (!valido(v)) return;
    ultimo = i;
    if (maior < 0 || v > (valores[maior] as number)) maior = i;
    if (v !== 0 && (menor < 0 || v < (valores[menor] as number))) menor = i;
  });
  return new Set([ultimo, maior, menor].filter((i) => i >= 0));
}

/** Só o último ponto com dado (linhas de meta e de acumulado: o fim é o que importa). */
export function ultimoPonto(valores: ReadonlyArray<unknown>): Set<number> {
  for (let i = valores.length - 1; i >= 0; i -= 1) {
    if (valido(valores[i])) return new Set([i]);
  }
  return new Set();
}

/** O filtro do rótulo: todos, ou só os índices escolhidos. */
export const mostrarIndices =
  (indices: Set<number>, todos: boolean) =>
  (i: number): boolean =>
    todos || indices.has(i);

/** Valores de uma chave numa série de pontos (o que o recharts desenha). */
export const valoresDe = <T extends object>(serie: ReadonlyArray<T>, chave: string): unknown[] =>
  serie.map((p) => (p as Record<string, unknown>)[chave]);

const nf1 = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });
const nf0 = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });

/** Em que grandeza os números das barras saem: milhões, milhares ou o valor cheio. */
export type Grandeza = 'milhões' | 'mil' | null;

export interface EscalaDoGrafico {
  formatar: (v: number) => string;
  grandeza: Grandeza;
}

/** Uma casa decimal abaixo do limite ("11,9", "0,8"); inteiro dali para cima ("273"). */
const compacto = (v: number, limite: number): string => (Math.abs(v) < limite ? nf1.format(v) : nf0.format(v));

/**
 * Coluna estreita (30 dias, ou 12 meses com duas barras cada) não tem largura para "273 mil" ou
 * "R$ 11,9 mi": o número sai na grandeza do maior valor, sem o sufixo ("273", "11,9", "0,8"), e a
 * grandeza vai uma vez só, no título ou na legenda. `ateMil` não sobe para milhões (o dia de uma
 * linha não passa de centenas de milhares). Uma casa decimal abaixo de `casaAbaixoDe` (100: "11,9
 * mi" não vira "12"); a coluna diária, mais estreita, usa 10. Zero não ganha número.
 */
export function escalaDoGrafico(
  valores: ReadonlyArray<number>,
  { ateMil = false, casaAbaixoDe = 100 } = {},
): EscalaDoGrafico {
  const maior = Math.max(0, ...valores.filter(Number.isFinite).map(Math.abs));
  const [divisor, grandeza]: [number, Grandeza] =
    maior >= 1_000_000 && !ateMil ? [1_000_000, 'milhões'] : maior >= 1000 ? [1000, 'mil'] : [1, null];
  return { formatar: (v) => (v === 0 ? '' : compacto(v / divisor, casaAbaixoDe)), grandeza };
}

/** Como escrever o número de cada coluna diária, e a unidade que vai no título do gráfico. */
export interface EscalaDoDia {
  formatar: (v: number) => string;
  unidade: string;
}

/** A coluna do dia: milhares ("mil unidades") ou, na linha pequena, unidades. */
export function escalaDoDia(valores: ReadonlyArray<number>): EscalaDoDia {
  const { formatar, grandeza } = escalaDoGrafico(valores, { ateMil: true, casaAbaixoDe: 10 });
  return { formatar, unidade: grandeza === 'mil' ? 'mil unidades' : 'unidades' };
}

/**
 * Como o número de cada coluna aparece (03/11/2026, pedido do pessoal: "o número tem que estar no
 * gráfico"): `deitado` quando cabe na largura da categoria; `em-pe` (girado 90°, em cima da coluna)
 * quando só cabe assim; `null` quando nem em pé cabe — aí o gráfico rola para o lado (celular).
 */
export type ModoRotulo = 'deitado' | 'em-pe' | null;

/**
 * Pixels que o gráfico precisa: `reservado` (eixo e margens) e, por categoria, `deitado` para o
 * número deitado e `emPe` para o número em pé. Sem `emPe`, o gráfico não tem o modo em pé (barra
 * horizontal, por exemplo).
 */
export interface MedidasRotulo {
  reservado: number;
  deitado: number;
  emPe?: number;
}

/** O modo do rótulo para a largura do gráfico. Largura 0 (não medida, ou nos testes) conta como larga. */
export function modoDoRotulo(largura: number, categorias: number, { reservado, deitado, emPe }: MedidasRotulo): ModoRotulo {
  if (largura <= 0 || categorias <= 0) return 'deitado';
  const porCategoria = (largura - reservado) / categorias;
  if (porCategoria >= deitado) return 'deitado';
  if (emPe !== undefined && porCategoria >= emPe) return 'em-pe';
  return null;
}

/**
 * Largura mínima do gráfico quando nem em pé o número cabe na largura disponível: a que deixa
 * todos deitados. O gráfico fica dessa largura e rola para o lado. `null` = não precisa rolar.
 */
export function larguraParaRolar(disponivel: number, categorias: number, medidas: MedidasRotulo): number | null {
  if (modoDoRotulo(disponivel, categorias, medidas) !== null) return null;
  return Math.ceil(medidas.reservado + categorias * medidas.deitado);
}

/**
 * Espaço acima do gráfico para o número em pé mais comprido: em pé, o texto cresce para cima a
 * partir do topo da coluna. ~0,62 da fonte por caractere (Space Grotesk, peso 600) + folga.
 */
export function margemDoRotuloEmPe(textos: ReadonlyArray<string>, fonte: number, minimo = 0): number {
  const maior = textos.reduce((m, t) => Math.max(m, t.length), 0);
  return Math.max(minimo, Math.ceil(maior * fonte * 0.62) + 14);
}
